import { tt } from '../i18n'

/**
 * Whether an order's fiscal receipt can be reversed - `FRONTEND.md` §9.442, FB-761.
 *
 * **The rule is spelled once here because it is about to be asked in five places.** The owner asked
 * for *Storno fiškálneho* on the dispatch board, in each order, in *Otvorené stoly*, in the payment
 * dialog and on the table map; `lists/OrderRow.tsx` already had it. Five copies of a rule about
 * reversing a registered sale is five chances to spell it differently, and this codebase already had
 * two spellings that disagree - see the note on `fiskalVirtual` below.
 *
 * **The fields come off `GET /orders`, the list read**, and that is not incidental: `OrderDtos`'s own
 * comment says the list *"answers the whole row - every fiscal, delivery-run and SMS column - because
 * the dispatch board and the archive need them"*, while the single-order read **drops every
 * `fiskal*` column**. All five surfaces read the list. A screen written against the detail read
 * could never show this button.
 */
export interface FiskalFacts {
  fiskalPrintedAt?: string | null
  fiskalStornoAt?: string | null
  fiskalVirtual?: boolean
}

/**
 * True when there is a fiscal document to reverse and nobody has reversed it.
 *
 * **`fiskalVirtual` is deliberately not consulted, and that is the one open question.** Two rules
 * already existed: `OrderRow`'s allowed a virtual receipt, and `FiskalReceiptsPage`'s
 * `canStornoOnDevice` refuses one because *"the register does not know it exists, so there is nothing
 * on the device to reverse"*. **The server accepts it** - it marks the order reversed locally and
 * answers with a message telling the operator to storno from the register's own menu.
 *
 * **Measured on the owner's data: 2 237 of 6 668 printed receipts are virtual, and 2 217 of those
 * are not reversed.** So refusing them would hide the button on 2 217 orders where the local marking
 * is the only way to make the books agree. This keeps `OrderRow`'s existing rule so that nothing
 * regresses, and the choice is `OWNER-QUESTIONS.md` #38 with that figure rather than a decision
 * taken while porting.
 */
export function mayStornoFiskal(order: FiskalFacts): boolean {
  return hasLiveFiskalDocument(order)
}

/**
 * Whether the register holds a document for this order that nobody has cancelled - §9.568, FB-863.
 *
 * **The same expression as {@link mayStornoFiskal}, named for the fact rather than for the button.**
 * That matters because the fact now decides four other things: whether the order editor may move the
 * money (§9.567), how much of a handover the register actually registered (§9.568), what the archive
 * row warns about (§9.569), and - on the server - whether §10A.292 refuses an edit at all.
 *
 * **`mayStornoFiskal` delegates rather than being replaced**, because its five call sites are about
 * offering a control and its own javadoc carries the `fiskalVirtual` question (#38) that belongs with
 * them. One implementation, two names, and the names say which question is being asked.
 *
 * **This was very nearly a second copy.** §9.567 declared it in `manager-orders` minutes before
 * FB-863 went looking for a shared home and found this file - `agos-one-guard-spelled-twice`, on a
 * predicate this repository already spells inline on the dispatch board and in the archive row.
 */
export function hasLiveFiskalDocument(order: FiskalFacts): boolean {
  const printed = (order.fiskalPrintedAt ?? '') !== ''
  const reversed = (order.fiskalStornoAt ?? '') !== ''
  return printed && !reversed
}

/**
 * What a reversal that has already happened says, or `''`.
 *
 * Shown where there is room for it, because the answer to *why is the button gone* is *somebody has
 * already done it*, and the name is the point: `POST /fiskal/storno` re-authenticates whoever
 * authorised the reversal precisely so the document carries it.
 */
export function stornoStamp(
  order: FiskalFacts & { fiskalStornoBy?: string | null },
  when: (iso: string) => string,
): string {
  const reversedAt = order.fiskalStornoAt ?? ''
  if (reversedAt === '') return ''
  const who = order.fiskalStornoBy ?? ''
  return `${tt('Stornované')} ${when(reversedAt)}${who === '' ? '' : ` · ${who}`}`
}
